// Seznam produktů v sadě (modal sady) — s přetahováním pořadí za úchyt ⋮⋮.
// Pořadí = pořadí kroků ve widgetu na e-shopu. Stejný vzor drag & drop jako
// členové propojení (LinkGroupsList.tsx), bez knihovny navíc.

import { useRef, useState } from 'react';

export type SelectedItem = {
  shopitem_id: number;
  parent_code: string | null;
  name: string;
  image_url: string | null;
  item_id: number | null; // null = nově přidaný, číslo = existující v DB
  display_name: string; // vlastní název ve widgetu — prázdné = použij `name`
  original_display_name: string; // pro detekci změny při editu (PATCH jen když se liší)
  original_order: number | null; // display_order v DB — PATCH, jen když se pořadí změnilo
};

type Props = {
  items: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
};

export function ProductSetItemsList({ items, onChange }: Props) {
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  function moveTo(idx: number) {
    if (draggingIdx === null || draggingIdx === idx) return;
    const arr = [...items];
    const [moved] = arr.splice(draggingIdx, 1);
    if (moved) arr.splice(idx, 0, moved);
    onChange(arr);
    setDraggingIdx(idx);
  }

  function remove(shopitem_id: number) {
    onChange(items.filter((s) => s.shopitem_id !== shopitem_id));
  }

  function setDisplayName(shopitem_id: number, value: string) {
    onChange(items.map((s) => (s.shopitem_id === shopitem_id ? { ...s, display_name: value } : s)));
  }

  return (
    <div className="mt-2 space-y-1.5">
      <div className="text-xs text-text-muted">
        Vybráno: <strong className="text-text">{items.length}</strong>
        {items.length > 1 && <span> · pořadí změníš přetažením za ⋮⋮</span>}
      </div>
      {items.map((it, idx) => (
        <ItemCard
          key={it.shopitem_id}
          item={it}
          isDragging={draggingIdx === idx}
          onDragStart={() => setDraggingIdx(idx)}
          onDragOver={() => moveTo(idx)}
          onDragEnd={() => setDraggingIdx(null)}
          onRemove={() => remove(it.shopitem_id)}
          onDisplayName={(v) => setDisplayName(it.shopitem_id, v)}
        />
      ))}
    </div>
  );
}

type CardProps = {
  item: SelectedItem;
  isDragging: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragEnd: () => void;
  onRemove: () => void;
  onDisplayName: (value: string) => void;
};

function ItemCard({
  item: it,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
  onDisplayName,
}: CardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={cardRef}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        e.preventDefault();
        onDragEnd();
      }}
      className={`flex items-start gap-2 bg-bg border border-border rounded-lg px-2 py-2 transition-opacity ${
        isDragging ? 'opacity-40 ring-2 ring-primary' : ''
      }`}
    >
      <button
        type="button"
        draggable
        onDragStart={(e) => {
          if (cardRef.current) e.dataTransfer.setDragImage(cardRef.current, 0, 0);
          e.dataTransfer.effectAllowed = 'move';
          e.stopPropagation();
          onDragStart();
        }}
        className="text-text-muted hover:text-text px-1.5 py-2 cursor-grab active:cursor-grabbing select-none leading-none"
        aria-label="Přesunout pořadí"
        title="Přetažením změníš pořadí"
      >
        ⋮⋮
      </button>
      {it.image_url ? (
        <img
          src={it.image_url}
          alt=""
          className="w-8 h-8 rounded object-contain bg-surface flex-shrink-0 mt-0.5"
        />
      ) : (
        <div className="w-8 h-8 rounded bg-surface border border-border flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-text truncate">{it.name}</div>
        <div className="text-xs text-text-muted font-mono mb-1.5">
          {it.parent_code ?? '—'} · #{it.shopitem_id}
        </div>
        <input
          type="text"
          value={it.display_name}
          onChange={(e) => onDisplayName(e.target.value)}
          placeholder={`Název ve widgetu (výchozí: ${it.name})`}
          className="w-full text-xs text-text placeholder:text-text-muted bg-surface border border-border rounded px-2.5 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-xs font-medium text-text-secondary hover:text-danger cursor-pointer mt-0.5"
      >
        Odebrat
      </button>
    </div>
  );
}
